import type { RJSFSchema, WidgetProps } from '@rjsf/utils';
import { useId } from 'react';
import styled, { css } from 'styled-components';
import { TypographyVariant } from '../../../utils/theme';
import Checkbox from '../../checkbox/checkbox';
import Typography from '../../typography/typography';

interface CheckboxesWidgetProps extends Omit<WidgetProps, 'value'> {
  // Support selection of multiple values
  value?: string[];
}

const CheckboxesWidget = ({
  label,
  value,
  onChange,
  schema,
  disabled,
  readonly,
}: CheckboxesWidgetProps) => {
  const titleId = useId();
  const options = schema.items.anyOf as RJSFSchema[];
  const currentValue = value ?? [];

  const handleChange = (checked: boolean, option: string) => {
    const newValues = checked
      ? [...currentValue, option]
      : currentValue.filter((item: string) => item !== option);
    onChange(newValues);
  };

  return (
    <>
      <TypographyContainer>
        <Typography variant={TypographyVariant.bodySm2} id={titleId}>
          {label}
        </Typography>
        <Typography variant={TypographyVariant.bodySm1}>
          {schema?.description}
        </Typography>
      </TypographyContainer>

      <CheckboxContainer>
        {options?.map((option) => (
          <Checkbox
            key={option.title}
            label={option.title ?? ''}
            value={currentValue.includes(option.const as string)}
            onChange={(checked) =>
              handleChange(checked, option.const as string)
            }
            disabled={disabled || readonly}
          />
        ))}
      </CheckboxContainer>
    </>
  );
};

export default CheckboxesWidget;

const CheckboxContainer = styled.div`
  ${({ theme }) => css`
    display: grid;
    grid-gap: ${theme.spacing(1.5)};
    grid-template-rows: repeat(9, min-content);
    grid-auto-flow: column;
  `}
`;

const TypographyContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};
    margin-bottom: ${theme.spacing(1.5)};
  `}
`;
